<script setup lang="ts">
import { ref, computed } from 'vue';
const route = useRoute();

import { splitPhoneNumber } from '@/scripts/utils';
import { countries } from '@/scripts/countries';
import useOrigins from '@/composables/useOrigins';

const emit = defineEmits(['show-login']);

const email = ref(undefined);
const name = ref(undefined);
const number = ref<string | undefined>(undefined);
const password = ref(undefined);
const loading = ref(false);
const showExtras = ref(false);

const country = ref('MT');
const currency = ref('EUR');
const language = ref('en');
const birthDate = ref('1990-02-03');
const sex = ref('Female');
const city = ref('Valletta');
const postalCode = ref('VLT 1000');
// Optional player-chosen username. Defaults to empty string so existing flows
// (skipped extras section, no username provided) are unaffected. Surfaces on
// tournament leaderboards when an operator opts the tournament into
// "Platform Username" mode (CU-DEV-17389).
const username = ref('');
// Per-user origin. operator-test-api keeps it only if it is on its USER_ORIGINS
// allowlist, otherwise it registers the user as starry-spins.
const { origins, defaultOrigin } = useOrigins();
const origin = ref(defaultOrigin);

const canSave = computed(() => {
  if (!email.value) return false;
  if (!name.value) return false;
  if (!number.value) return false;
  if (!password.value) return false;

  return true;
});


const handleCreateAccount = async () => {
  loading.value = true;
  const splitName = name.value.split(' ');
  let phoneNumber;
  try {
    phoneNumber = splitPhoneNumber(number.value.toString());
  } catch (e) {
    alert('Phone number is in the wrong format');
    loading.value = false;
    return;
  }
  const payload: Record<string, string> = {
    email: email.value,
    firstname: splitName[0],
    lastname: splitName[1] || '',
    mobile_prefix: phoneNumber.prefix,
    mobile: phoneNumber.number,
    affiliate_reference: route.query.btag?.toString() || 'wges-direct',
    origin: origin.value,
  }

  if (showExtras.value) {
    if (country.value) payload.country = country.value;
    if (currency.value) payload.currency = currency.value;
    if (language.value) payload.language = language.value;
    if (birthDate.value) payload.birth_date = birthDate.value;
    if (sex.value) payload.sex = sex.value;
    if (city.value) payload.city = city.value;
    if (postalCode.value) payload.postal_code = postalCode.value;
    if (username.value) payload.username = username.value;
  }

  const req = await fetch('/api/user/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  loading.value = false;
  if (!req.ok) {
    console.error(await req.json());
    if (req.status === 409) {
      alert('Email already exists')
    } else {
      alert('Could not create account')
    }
    return;
  }

  emit('show-login');
}

</script>

<template>
  <div class="create-account-form">
    <form @submit.prevent="handleCreateAccount">
      <div class="form-row">
        <label>
          <div class="input-label">
            Email
          </div>
          <div class="input-wrapper">
            <div class="input-wrapper__icon">
              <fa :icon="['fasl', 'envelope']"></fa>
            </div>
            <input type="email" v-model="email" placeholder="Enter email address" class="input">
          </div>
        </label>
      </div>
      <div class="form-row">
        <label>
          <div class="input-label">
            Password
          </div>
          <div class="input-wrapper">
            <div class="input-wrapper__icon">
              <fa :icon="['fasl', 'lock']"></fa>
            </div>
            <input type="password" v-model="password" placeholder="Enter your password" class="input">
          </div>
        </label>
      </div>
      <div class="form-row">
        <label>
          <div class="input-label">
            Name
          </div>
          <div class="input-wrapper">
            <div class="input-wrapper__icon">
              <fa :icon="['fasl', 'user']"></fa>
            </div>
            <input type="text" v-model="name" placeholder="Enter a name" class="input">
          </div>
        </label>
      </div>
      <div class="form-row">
        <label>
          <div class="input-label">
            Phone number
          </div>
          <div class="input-wrapper">
            <div class="input-wrapper__icon">
              <fa :icon="['fasl', 'phone']"></fa>
            </div>
            <input type="tel" v-model="number" placeholder="Enter a phone number" class="input">
          </div>
        </label>
        <div class="phone-number-warning">
          Phone number should be in the format +46xxxxxxxxx
        </div>
      </div>
      <div class="extras-toggle" @click="showExtras = !showExtras">
        <fa :icon="['fasl', showExtras ? 'chevron-up' : 'chevron-down']" class="extras-toggle__icon"></fa>
        Additional Details
      </div>
      <div v-if="showExtras" class="extras-section">
        <div class="form-row">
          <label>
            <div class="input-label">Username <span class="input-label__hint">(optional)</span></div>
            <div class="input-wrapper">
              <div class="input-wrapper__icon">
                <fa :icon="['fasl', 'id-badge']"></fa>
              </div>
              <input type="text" v-model="username" placeholder="Pick a display name" class="input">
            </div>
          </label>
        </div>
        <div class="form-row">
          <label>
            <div class="input-label">Origin</div>
            <div class="input-wrapper">
              <div class="input-wrapper__icon">
                <fa :icon="['fasl', 'tag']"></fa>
              </div>
              <select v-model="origin" data-test="origin-input" class="input">
                <option v-for="o in origins" :key="o.key" :value="o.key">
                  {{ o.name }}
                </option>
              </select>
            </div>
          </label>
        </div>
        <div class="extras-row">
          <div class="form-row">
            <label>
              <div class="input-label">Country</div>
              <div class="input-wrapper">
                <div class="input-wrapper__icon">
                  <fa :icon="['fasl', 'globe']"></fa>
                </div>
                <select v-model="country" class="input">
                  <option value="">-- Select --</option>
                  <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.name }}</option>
                </select>
              </div>
            </label>
          </div>
          <div class="form-row">
            <label>
              <div class="input-label">Currency</div>
              <div class="input-wrapper">
                <div class="input-wrapper__icon">
                  <fa :icon="['fasl', 'coins']"></fa>
                </div>
                <select v-model="currency" class="input">
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="SEK">SEK</option>
                  <option value="NOK">NOK</option>
                  <option value="DKK">DKK</option>
                  <option value="CHF">CHF</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                  <option value="JPY">JPY</option>
                </select>
              </div>
            </label>
          </div>
        </div>
        <div class="extras-row">
          <div class="form-row">
            <label>
              <div class="input-label">Language</div>
              <div class="input-wrapper">
                <div class="input-wrapper__icon">
                  <fa :icon="['fasl', 'language']"></fa>
                </div>
                <select v-model="language" class="input">
                  <option value="en">English</option>
                  <option value="sv">Swedish</option>
                  <option value="no">Norwegian</option>
                  <option value="da">Danish</option>
                  <option value="fi">Finnish</option>
                  <option value="de">German</option>
                  <option value="fr">French</option>
                  <option value="es">Spanish</option>
                  <option value="pt">Portuguese</option>
                  <option value="it">Italian</option>
                </select>
              </div>
            </label>
          </div>
          <div class="form-row">
            <label>
              <div class="input-label">Sex</div>
              <div class="input-wrapper">
                <div class="input-wrapper__icon">
                  <fa :icon="['fasl', 'venus-mars']"></fa>
                </div>
                <select v-model="sex" class="input">
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </label>
          </div>
        </div>
        <div class="form-row">
          <label>
            <div class="input-label">Birth Date</div>
            <div class="input-wrapper">
              <div class="input-wrapper__icon">
                <fa :icon="['fasl', 'cake-candles']"></fa>
              </div>
              <input type="date" v-model="birthDate" class="input">
            </div>
          </label>
        </div>
        <div class="extras-row">
          <div class="form-row">
            <label>
              <div class="input-label">City</div>
              <div class="input-wrapper">
                <div class="input-wrapper__icon">
                  <fa :icon="['fasl', 'city']"></fa>
                </div>
                <input type="text" v-model="city" placeholder="City" class="input">
              </div>
            </label>
          </div>
          <div class="form-row">
            <label>
              <div class="input-label">Postal Code</div>
              <div class="input-wrapper">
                <div class="input-wrapper__icon">
                  <fa :icon="['fasl', 'mailbox']"></fa>
                </div>
                <input type="text" v-model="postalCode" placeholder="Postal code" class="input">
              </div>
            </label>
          </div>
        </div>
      </div>
      <div class="button-wrapper">
        <button class="button" type="submit" style="width: 100%" :disabled="!canSave || loading"
          :class="{ 'button--loading': loading }">Register</button>
        <div class="create-account-button" @click="$emit('show-login')">
          Login
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
.create-account-form {
  width: 100%;
  max-width: 340px;
}

.phone-number-warning {
  font-size: 13px;
  margin-top: 4px;
}

.extras-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #9D9FB5;
  margin: 16px 0 8px;
  user-select: none;
}

.extras-toggle:hover {
  color: #fff;
}

.extras-toggle__icon {
  font-size: 12px;
}

.extras-section {
  margin-bottom: 4px;
}

.extras-row {
  display: flex;
  gap: 12px;
}

.extras-row .form-row {
  flex: 1;
  min-width: 0;
}

.input-label__hint {
  font-weight: normal;
  opacity: 0.6;
  font-size: 0.85em;
  margin-left: 4px;
}
</style>